import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  CircleAlert,
  Clock,
  MailCheck,
  ShieldCheck,
  Ticket,
  UserPlus,
} from 'lucide-react';
import type { MagicLinkRequestResponse } from '../../shared/api';
import type { WorkspaceRole } from '../../shared/types';
import { AuthLayout } from '../components/layout';
import { Alert, Avatar, Button, CopyButton, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { formatDay } from '../lib/format';
import { useSessionStore } from '../stores/session';
import { useWorkspaceStore } from '../stores/workspace';

const ROLE_BLURB: Record<WorkspaceRole, string> = {
  owner: 'Full control, including billing and ownership transfer.',
  admin: 'Create and publish posts, manage connections, invite teammates.',
  member: 'Create and edit posts, and use everything the workspace has connected.',
};

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const user = useSessionStore((state) => state.user);
  const status = useSessionStore((state) => state.status);
  const authMethods = useSessionStore((state) => state.authMethods);
  const refresh = useSessionStore((state) => state.refresh);
  const signOut = useSessionStore((state) => state.signOut);
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);

  const [magicLink, setMagicLink] = useState<MagicLinkRequestResponse | null>(null);

  const preview = useQuery({
    queryKey: qk.invitePreview(token ?? ''),
    queryFn: () => api.invites.lookup(token as string),
    enabled: Boolean(token),
    retry: false,
  });

  const invite = preview.data?.invite ?? null;

  const sendMagicLink = useMutation({
    mutationFn: (email: string) =>
      api.auth.requestMagicLink({
        email,
        inviteToken: token as string,
        redirect: '/onboarding',
      }),
    onSuccess: (response) => setMagicLink(response),
  });

  const accept = useMutation({
    mutationFn: () => api.invites.accept({ token: token as string }),
    onSuccess: async (response) => {
      setWorkspace(response.workspace.id);
      await refresh();
      navigate('/', { replace: true });
    },
    onError: (error) => {
      // Accepting requires a session on this deployment — fall back to email.
      if (error instanceof ApiError && error.isUnauthorized && invite?.email) {
        sendMagicLink.mutate(invite.email);
      }
    },
  });

  /* ---------------- states without a usable invite ---------------- */

  if (!token) {
    return (
      <AuthLayout
        title="No invitation token"
        subtitle="This page needs the token that came with your invitation link."
        footer={
          <>
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-accent hover:underline">
              Sign in
            </Link>
          </>
        }
      >
        <Alert tone="warning" title="Open the link from your invitation email" icon={<Ticket />}>
          It looks like <code className="font-mono">?token=…</code> was trimmed when the link was
          copied. Ask whoever invited you to resend it.
        </Alert>
      </AuthLayout>
    );
  }

  if (preview.isLoading) {
    return (
      <AuthLayout title="Checking your invitation" subtitle="Looking up the workspace you were invited to.">
        <div className="flex justify-center py-6">
          <Spinner label="One moment" />
        </div>
      </AuthLayout>
    );
  }

  if (preview.error || !invite) {
    return (
      <AuthLayout
        title="That invitation is not valid"
        subtitle="It may have been revoked, or the link may be incomplete."
        footer={
          <>
            Have an account already?{' '}
            <Link to="/login" className="font-medium text-accent hover:underline">
              Sign in
            </Link>
          </>
        }
      >
        <Alert tone="danger" title="We could not find this invitation" icon={<CircleAlert />}>
          Ask a workspace owner or admin to send you a fresh invite from their Team page.
        </Alert>
      </AuthLayout>
    );
  }

  if (invite.accepted) {
    return (
      <AuthLayout
        title="This invitation was already used"
        subtitle={`${invite.email} is already a member of ${invite.workspaceName}.`}
      >
        <Button fullWidth size="lg" onClick={() => navigate('/login', { replace: true })}>
          Sign in instead
        </Button>
      </AuthLayout>
    );
  }

  if (invite.expired) {
    return (
      <AuthLayout
        title="This invitation has expired"
        subtitle={`It was valid until ${formatDay(invite.expiresAt)}.`}
      >
        <Alert tone="warning" title="Ask for a new link" icon={<Clock />}>
          {invite.invitedByName
            ? `${invite.invitedByName} can send another invitation from the Team page of ${invite.workspaceName}.`
            : `An owner or admin of ${invite.workspaceName} can send another invitation.`}
        </Alert>
      </AuthLayout>
    );
  }

  /* ---------------- magic-link sent ---------------- */

  if (magicLink) {
    return (
      <AuthLayout
        title="Check your inbox"
        subtitle={`We sent a sign-in link to ${invite.email}. Opening it accepts the invitation.`}
      >
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-3 py-2 text-center">
            <span className="flex size-11 items-center justify-center rounded-full bg-accent-soft text-accent">
              <MailCheck className="size-5" aria-hidden />
            </span>
            <p className="text-sm text-muted">{magicLink.message}</p>
          </div>
          {magicLink.devLink && (
            <Alert tone="info" title="Email delivery is not configured">
              <p>Use this one-time link instead:</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-surface px-2 py-1 font-mono text-[11px] text-fg">
                  {magicLink.devLink}
                </code>
                <CopyButton value={magicLink.devLink} />
              </div>
              <Button
                className="mt-2"
                size="sm"
                onClick={() => {
                  window.location.href = magicLink.devLink as string;
                }}
              >
                Open sign-in link
              </Button>
            </Alert>
          )}
        </div>
      </AuthLayout>
    );
  }

  /* ---------------- the invitation itself ---------------- */

  const signedIn = status === 'authenticated' && Boolean(user);
  const emailMatches = signedIn && user?.email?.toLowerCase() === invite.email.toLowerCase();
  const acceptError = accept.error instanceof ApiError ? accept.error : null;
  const showAcceptError = acceptError && !acceptError.isUnauthorized;

  return (
    <AuthLayout
      eyebrow="Invitation"
      title={`Join ${invite.workspaceName}`}
      subtitle={
        invite.invitedByName
          ? `${invite.invitedByName} invited you to collaborate on content.`
          : 'You have been invited to collaborate on content.'
      }
      footer={<>Invitations expire on {formatDay(invite.expiresAt)}.</>}
    >
      <div className="space-y-5">
        <div className="flex items-center gap-3 rounded-lg border border-border bg-elevated/50 p-3">
          <Avatar name={invite.workspaceName} size="md" shape="square" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-fg">{invite.workspaceName}</p>
            <p className="truncate text-xs text-muted">
              Joining as <span className="capitalize text-fg">{invite.role}</span> · {invite.email}
            </p>
          </div>
        </div>

        <p className="flex items-start gap-2 text-xs text-muted">
          <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-subtle" aria-hidden />
          {ROLE_BLURB[invite.role]}
        </p>

        {showAcceptError && (
          <Alert tone="danger" title="Could not join the workspace">
            {acceptError.message}
          </Alert>
        )}

        {signedIn && !emailMatches && (
          <Alert tone="warning" title={`You are signed in as ${user?.email}`}>
            Accepting now adds <span className="font-medium">{user?.email}</span> to the workspace.
            Sign out first if you meant to join as {invite.email}.
          </Alert>
        )}

        <Button
          size="lg"
          fullWidth
          loading={accept.isPending || sendMagicLink.isPending}
          loadingLabel="Joining the workspace"
          leftIcon={<UserPlus className="size-4" />}
          rightIcon={<ArrowRight className="size-4" />}
          onClick={() => accept.mutate()}
        >
          {signedIn ? 'Accept and join' : 'Accept invitation'}
        </Button>

        {!signedIn && (
          <>
            <p className="text-center text-xs text-subtle">
              We will email {invite.email} a sign-in link if this deployment requires one.
            </p>
            {authMethods.google && (
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  window.location.href = api.auth.googleStartUrl('/onboarding', token);
                }}
              >
                Continue with Google
              </Button>
            )}
          </>
        )}

        {signedIn && !emailMatches && (
          <Button
            variant="ghost"
            fullWidth
            onClick={async () => {
              await signOut();
              navigate(`/accept-invite?token=${encodeURIComponent(token)}`, { replace: true });
            }}
          >
            Sign out and use {invite.email}
          </Button>
        )}
      </div>
    </AuthLayout>
  );
}

export default AcceptInvitePage;
